import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2 } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { useMasterValues } from '../hooks/useMasterData';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';

const STATUSES = ['pending','in_progress','completed','cannot_repair','warranty_claim'];

export default function Repairs() {
  const repairVendors = useMasterValues('repair_vendor');
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState(''); const [filterWarranty, setFilterWarranty] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState(null); const [filterDept, setFilterDept] = useState(null);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [assets, setAssets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterStatus) params.status = filterStatus; if (filterWarranty) params.warranty_covered = filterWarranty;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(",") : filterCompany;
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/repairs', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterWarranty, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/assets', { params: { limit: 2000 } }).then(r => setAssets(r.data.data || [])).catch(() => {});
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    if (!form.asset_id) { setError('Asset is required.'); return; }
    if (!form.repair_cost) { setError('Repair Cost is required.'); return; }
    setSaving(true); setError('');
    try {
      selected ? await api.put(`/repairs/${selected.id}`, form) : await api.post('/repairs', form);
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'Asset', render: r => r.Asset ? <div><div className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.Asset.asset_number}</div><div className="text-xs" style={{color:'#475569'}}>{r.Asset.brand} {r.Asset.model}</div></div> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Issue', render: r => <span className="text-xs" style={{color:'#94a3b8'}}>{r.issue_description}</span> },
    { header: 'Vendor', render: r => r.repair_vendor || <span style={{color:'#334155'}}>—</span> },
    { header: 'Cost', render: r => r.repair_cost ? <span style={{color:'#f87171'}}>LKR {Number(r.repair_cost).toLocaleString()}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Status', render: r => <Badge value={r.status}/> },
    { header: 'Warranty', render: r => <span style={{color:r.warranty_covered?'#34d399':'#334155'}}>{r.warranty_covered?'Yes':'No'}</span> },
    { header: 'Reported', key: 'reported_date' },
    { header: '', render: r => isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button> }
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Repairs" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({status:'pending',reported_date:new Date().toISOString().split('T')[0]}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Repair</button>}
        </div>
      </PageHeader>
      <FilterBar hasFilters={!!(search||filterStatus||filterWarranty||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus(''); setFilterWarranty(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search issue, vendor, resolution..." />
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s => ({value:s,label:s.replace('_',' ')}))} />
        <FilterSelect value={filterWarranty} onChange={v => { setFilterWarranty(v); setPage(1); }} placeholder="Warranty: All" options={[{value:'true',label:'Warranty Covered'},{value:'false',label:'Not Covered'}]} />
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>
      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage} />
      {modal === 'form' && (
        <Modal title={selected ? 'Edit Repair' : 'New Repair'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error} />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Asset *</label>
                <SearchableSelect required value={form.asset_id||''} onChange={v=>setForm({...form,asset_id:v})} placeholder="Select asset..."
                  options={assets.map(a=>({value:a.id,label:`${a.asset_number} — ${a.brand} ${a.model}${a.serial_number?` (S/N: ${a.serial_number})`:''}`}))}/>
              </div>
              <FF label="Reported Date *" name="reported_date" type="date" form={form} setForm={setForm} required />
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Repair Vendor</label><select value={form.repair_vendor||''} onChange={e=>setForm({...form,repair_vendor:e.target.value})} className="input-dark"><option value="">Select vendor</option>{repairVendors.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
              <FF label="Repair Cost (LKR) *" name="repair_cost" type="number" form={form} setForm={setForm} required />
              <FS label="Status" name="status" form={form} setForm={setForm} options={STATUSES.map(s => ({val:s,label:s.replace('_',' ')}))} />
              <FS label="Warranty Covered" name="warranty_covered" form={form} setForm={setForm} options={[{val:'true',label:'Yes'},{val:'false',label:'No'}]} />
              <FF label="Started Date" name="started_date" type="date" form={form} setForm={setForm} />
              <FF label="Completed Date" name="completed_date" type="date" form={form} setForm={setForm} />
            </div>
            <FF label="Issue Description *" name="issue_description" form={form} setForm={setForm} rows={4} required placeholder="Describe the issue in detail..." />
            <FF label="Resolution Notes" name="resolution" form={form} setForm={setForm} rows={4} placeholder="How was the issue resolved?" />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
